import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import clipboard from 'ember-cli-clipboard/modifiers/clipboard';
import { task } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import { t } from 'ember-intl';

import Button from 'umg-ember-backend/components/ui/button';
import { Codemirror } from 'umg-ember-backend/components/ui/codemirror';
import Form from 'umg-ember-backend/components/ui/form';
import ENV from 'umg-ember-backend/config/environment';
import { getBem } from 'umg-ember-backend/utils/get-bem';

import AssetInformation from '../asset-information';
import styles from './styles.module.css';

const bem = getBem(styles);

class EmbedCodeSettings {
  // Defaults
  id = 0;
  recordType = 'embed-code';
  @tracked width = '0';
  @tracked height = '0';

  // Constructor
  constructor(asset) {
    this.asset = asset;
    this.width = asset.sourceFormat.width ?? '1024';
    this.height = asset.sourceFormat.height ?? '768';
  }

  // Functions
  toJSON() {
    let { asset, width, height } = this;

    return `${asset.id}|${width}|${height}`;
  }
}

export default class AssetToolsEmbedCodeComponent extends Component {
  // Services
  @service flashMessages;

  // Defaults
  embedCodeSettings = new EmbedCodeSettings(this.args.asset);
  @tracked embedCode = null;

  // Tasks
  generateEmbedCode = task({ restartable: true }, async () => {
    try {
      const response = await fetch(
        `${
          ENV.mediaserviceHost
        }/player/encryptData?data=${this.embedCodeSettings.toJSON()}`,
      );
      const embedCode = await response.json();

      if (response.status === 400) {
        this.flashMessages.error(embedCode.errors.message, {
          rawString: true,
        });
      }

      if (embedCode.encryptedData) {
        this.embedCode = `<script type="text/javascript" src="${ENV.mediaserviceHost}/player/embedCode?id=${embedCode.encryptedData}"></script>`;
      }
    } catch (error) {
      this.flashMessages.serverError(error);
    }
  });

  // Template
  <template>
    <div class={{(bem)}} ...attributes>
      {{#if this.embedCode}}
        <AssetInformation
          @asset={{@asset}}
          @copySuccess={{@copySuccess}}
          @copyError={{@copyError}}
        />

        <label>
          {{t 'asset_tools.embed_code.results.embed_code'}}
        </label>
        <div class={{bem 'result'}}>
          <Codemirror @value={{this.embedCode}} @readOnly={{true}} />
          <Button
            @label={{t 'asset_tools.embed_code.copy'}}
            class={{bem 'copy'}}
            data-test-asset-tool-embed-code-copy-button
            {{clipboard
              text=this.embedCode
              onSuccess=@copySuccess
              onError=@copyError
            }}
          />
        </div>
      {{else}}
        <Form
          @disableRegisterToBreadcrumb={{true}}
          @model={{this.embedCodeSettings}}
          as |form|
        >
          <form.field @property='height' />
          <form.field @property='width' />
        </Form>

        <Button
          @label={{t 'asset_tools.embed_code.generate'}}
          {{on 'click' (perform this.generateEmbedCode)}}
        />
      {{/if}}
    </div>
  </template>
}
